/* eslint-disable max-len */
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Spinner } from "@xfrontend/xui-react-plus";
import { CraftyRobot } from "@xfrontend/xicon";
import ViolationPolicyTypeTag from "#/modules/common/ViolationPolicyTypeTag";
import { RiskLevelTag } from "#/modules/common/Tag";
import { getScoreColor } from "#/types/xecguard";

import { ApplicationCardData } from "../types";

const Card = styled.div`
  background-color: var(--x-purple-500, #494358);
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 100%;
  min-width: 296px;
  max-width: 468.5px;
  border-radius: 4px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--x-purple-400);
`;

const AppTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--x-gray-200);
  line-height: 1.4;
`;

const Organization = styled.div`
  font-size: 12px;
  color: var(--x-gray-300);
  padding-left: 22px;
`;

const ActivityRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActivityLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: var(--x-gray-200, #f6f6f7);
`;

const StatusBadge = styled.div<{ status: string }>`
  display: flex;
  padding: 2px 8px;
  justify-content: center;
  align-items: center;
  border-radius: var(--radius, 4px);
  background: ${(props: { status: string }) => (props.status === "Enabled"
    ? "#00adb8"
    : "var(--x-theme-secondary, #776890)")};
  color: var(--x-gray-200);
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
`;

const CardBody = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(73, 67, 88, 0.7);
  border-radius: 4px;
  z-index: 1;
`;

const ScoreCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  background: var(--x-bg-400, #3d364c);
  border-radius: 4px;
  padding: 12px;
`;

const ScoreValue = styled.div<{ color: string }>`
  font-size: 28px;
  font-weight: 700;
  color: ${(props: { color: string }) => props.color};
  line-height: 1;
`;

const ScoreLabel = styled.div`
  font-size: 12px;
  color: var(--x-gray-400);
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
`;

const StatItem = styled.div`
  display: flex;
  padding: 8px 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: var(--radius, 4px);
  background: var(--x-bg-400, #3d364c);
`;

const StatValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--x-gray-200);
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: var(--x-gray-400);
`;

const ViolationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ViolationTitle = styled.div`
  font-size: 11px;
  color: var(--x-gray-400);
`;

const ViolationTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

interface ApplicationCardProps {
  data: ApplicationCardData;
}

const ApplicationCard = ({ data }: ApplicationCardProps): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslation("applications");

  const score = data.secureScore;
  const scoreInfo = getScoreColor(score);

  const handleCardClick = (): void => {
    navigate(`/applications/${data.id}`);
  };

  return (
    <Card onClick={handleCardClick}>
      <CardHeader>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <CraftyRobot size={14} color="var(--x-gray-200)" />
          <AppTitle>{data.title}</AppTitle>
        </div>
        <Organization>{data.organization}</Organization>
      </CardHeader>

      <ActivityRow>
        <ActivityLabel>{t("card.last7DaysActivity")}</ActivityLabel>
        <StatusBadge status={data.status}>{data.status}</StatusBadge>
      </ActivityRow>

      <CardBody>
        {data.statsLoading && (
          <LoadingOverlay>
            <Spinner animation="border" variant="primary" />
          </LoadingOverlay>
        )}

        <ScoreCard>
          <ScoreValue color={scoreInfo.color}>{score}</ScoreValue>
          <ScoreLabel>{t("card.safetyScore")}</ScoreLabel>
        </ScoreCard>

        <StatsRow>
          <StatItem>
            <StatValue>{data.stats.totalRequests.value}</StatValue>
            <StatLabel>{t("card.totalRequests")}</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>{data.stats.unsafeRequests.value}</StatValue>
            <StatLabel>{t("card.policyViolations")}</StatLabel>
          </StatItem>
        </StatsRow>

        <ViolationSection>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <ViolationTitle>{t("card.violationPolicyTypes")}</ViolationTitle>
            {data.maxRiskLevel > 0 && <RiskLevelTag level={data.maxRiskLevel} />}
          </div>
          <ViolationTags>
            {data.violationTypes.map((v) => (
              <ViolationPolicyTypeTag key={v.label} index={v.tagIndex}>
                {v.label}
              </ViolationPolicyTypeTag>
            ))}
          </ViolationTags>
        </ViolationSection>
      </CardBody>
    </Card>
  );
};

export default ApplicationCard;
